import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppSettings } from '@/hooks/useAppSettings'
import { useAuth } from '@/hooks'
import { SectionCard } from '@/components/shared/section-card'
import { WatchModelsEditor } from '@/components/settings/watch-models-editor'
import { DEFAULT_PARLENT_WATCH_MODELS } from '@/lib/watch-models'
import { 
  Smartphone, 
  Apple, 
  AlertCircle, 
  CheckCircle2, 
  Save, 
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
  Power,
  Lock,
  User,
  Mail,
  Phone,
  MessageSquare,
  Watch,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/format'
import { getDocument, upsertDocument } from '@/lib/firestore-crud'

type TabType = 'version' | 'contact' | 'watch-models'

interface VersionEditorProps {
  platform: 'android' | 'ios'
  data: any
  onChange: (data: any) => void
}

function VersionEditor({ platform, data, onChange }: VersionEditorProps) {
  const isAndroid = platform === 'android'
  const Icon = isAndroid ? Smartphone : Apple
  const name = isAndroid ? 'Android' : 'iOS'
  const colorClass = isAndroid ? 'text-emerald-400' : 'text-blue-400'
  const bgClass = isAndroid ? 'bg-emerald-500/10' : 'bg-blue-500/10'

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', bgClass)}>
          <Icon className={cn('h-5 w-5', colorClass)} />
        </div>
        <div>
          <h3 className="font-bold text-white">{name}</h3>
          <p className="text-sm text-stone-500">Version control & settings</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-stone-400 mb-2">Current Version</label>
          <input
            type="text"
            value={data?.version || ''}
            onChange={(e) => onChange({ ...data, version: e.target.value })}
            placeholder="1.2.3"
            className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
          />
          <p className="text-xs text-stone-600 mt-1">Format: x.y.z</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-400 mb-2">Build Number</label>
          <input
            type="number"
            value={data?.buildNumber || ''}
            onChange={(e) => onChange({ ...data, buildNumber: parseInt(e.target.value) })}
            placeholder="123"
            className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-400 mb-2">Minimum Required Version</label>
          <input
            type="text"
            value={data?.minVersion || ''}
            onChange={(e) => onChange({ ...data, minVersion: e.target.value })}
            placeholder="1.0.0"
            className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
          />
          <p className="text-xs text-stone-600 mt-1">Users below this will be forced to update</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-stone-400 mb-2">Store URL</label>
          <div className="relative">
            <input
              type="url"
              value={data?.storeUrl || ''}
              onChange={(e) => onChange({ ...data, storeUrl: e.target.value })}
              placeholder={isAndroid 
                ? 'https://play.google.com/store/apps/details?id=com.parlent.fit'
                : 'https://apps.apple.com/app/parlent-fit/id1234567890'
              }
              className="w-full rounded-xl border border-stone-800 bg-stone-900 pl-4 pr-10 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
            />
            <a 
              href={data?.storeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="absolute right-3 top-3 text-stone-500 hover:text-white"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-stone-400 mb-2">Update Title</label>
          <input
            type="text"
            value={data?.updateTitle || ''}
            onChange={(e) => onChange({ ...data, updateTitle: e.target.value })}
            placeholder="Update Available"
            className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-stone-400 mb-2">Update Message</label>
          <textarea
            value={data?.updateMessage || ''}
            onChange={(e) => onChange({ ...data, updateMessage: e.target.value })}
            placeholder="A new version is available with bug fixes and improvements."
            rows={3}
            className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div className="md:col-span-2">
          <button
            onClick={() => onChange({ ...data, forceUpdate: !data?.forceUpdate })}
            className={cn(
              'w-full flex items-center justify-between p-4 rounded-xl border transition',
              data?.forceUpdate 
                ? 'border-rose-500/30 bg-rose-500/10' 
                : 'border-stone-800 bg-stone-900/50'
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', data?.forceUpdate ? 'bg-rose-500/20' : 'bg-stone-800')}>
                {data?.forceUpdate ? (<Lock className="h-5 w-5 text-rose-400" />) : (<Power className="h-5 w-5 text-stone-400" />)}
              </div>
              <div className="text-left">
                <p className="font-medium text-white">Force Update</p>
                <p className="text-sm text-stone-500">
                  {data?.forceUpdate ? 'Users MUST update to continue using the app' : 'Users can skip the update'}
                </p>
              </div>
            </div>
            {data?.forceUpdate ? (<ToggleRight className="h-8 w-8 text-rose-400" />) : (<ToggleLeft className="h-8 w-8 text-stone-600" />)}
          </button>
        </div>
      </div>
    </div>
  )
}

export function AppSettingsPage() {
  const { isSuperAdmin } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const { settings, loading, saving, saveSettings } = useAppSettings()
  
  const tabs = useMemo(() => {
    const items = [
      { id: 'version' as TabType, label: 'Version Control', icon: Smartphone, show: isSuperAdmin },
      { id: 'contact' as TabType, label: 'Admin Contact', icon: User, show: true },
      { id: 'watch-models' as TabType, label: 'Watch Models', icon: Watch, show: isSuperAdmin },
    ]
    return items.filter(t => t.show)
  }, [isSuperAdmin])

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab') as TabType
    if (tabParam && tabs.some(t => t.id === tabParam)) return tabParam
    return tabs[0]?.id || 'contact'
  })

  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null)
  
  // Version editing state
  const [androidData, setAndroidData] = useState(settings.android)
  const [iosData, setIosData] = useState(settings.ios)

  // Admin contact state — shared with mobile via systemConfig/adminContact
  const [adminContact, setAdminContact] = useState({
    name: 'Parlent Support',
    email: 'support@parlent.com',
    supportEmail: 'support@parlent.com',
    phone: '',
    whatsapp: '',
  })
  const [loadingContact, setLoadingContact] = useState(false)
  const [savingContact, setSavingContact] = useState(false)

  // Sync local version state when settings load
  useEffect(() => {
    setAndroidData(settings.android)
    setIosData(settings.ios)
  }, [settings])

  // Sync activeTab with search params if they change externally
  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabType
    if (tabParam && tabs.some(t => t.id === tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam)
    }
  }, [searchParams, tabs, activeTab])

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  useEffect(() => {
    if (activeTab === 'contact') loadAdminContact()
  }, [activeTab])

  async function loadAdminContact() {
    setLoadingContact(true)
    try {
      const doc = await getDocument<any>('systemConfig', 'adminContact')
      if (doc) setAdminContact(prev => ({ ...prev, ...doc }))
    } catch (e) { console.error(e) }
    finally { setLoadingContact(false) }
  }

  async function saveAdminContact() {
    setSavingContact(true)
    try {
      await upsertDocument('systemConfig', 'adminContact', {
        ...adminContact,
        updatedAt: new Date().toISOString()
      })
      setNotification({ type: 'success', message: 'Kontak berhasil disimpan! Mobile app akan membaca data ini.' })
    } catch {
      setNotification({ type: 'error', message: 'Gagal menyimpan kontak admin.' })
    } finally {
      setSavingContact(false)
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const handleSaveVersion = async () => {
    const success = await saveSettings({ android: androidData, ios: iosData })
    if (success) {
      setNotification({ type: 'success', message: 'Settings saved successfully!' })
    } else {
      setNotification({ type: 'error', message: 'Failed to save settings' })
    }
    setTimeout(() => setNotification(null), 3000)
  }

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-6 w-6 text-orange-500 animate-spin" />
          <p className="text-stone-400">Loading settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-[36px] border border-orange-500/20 bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-8">
        <div className="inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-orange-400">
          App Management
        </div>
        <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-white">
          App Settings
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Kelola versi aplikasi, force update, dan informasi kontak support yang tampil di mobile.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-stone-800 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition ${
              activeTab === tab.id
                ? 'border-b-2 border-orange-500 text-orange-500'
                : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Notification */}
      {notification && (
        <div className={cn(
          'rounded-2xl p-4 flex items-center gap-3',
          notification.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
        )}>
          {notification.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {notification.message}
        </div>
      )}

      {/* === VERSION CONTROL TAB === */}
      {activeTab === 'version' && (
        <>
          {/* Status Overview */}
          <div className="grid gap-4 md:grid-cols-3">
            <SectionCard title="Android Version" description="Current release">
              <div className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-emerald-400" />
                <span className="text-2xl font-bold text-white">{settings.android?.version || 'N/A'}</span>
              </div>
              {settings.android?.forceUpdate && (
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-rose-400">
                  <Lock className="h-3 w-3" /> Force Update Active
                </span>
              )}
            </SectionCard>

            <SectionCard title="iOS Version" description="Current release">
              <div className="flex items-center gap-2">
                <Apple className="h-5 w-5 text-blue-400" />
                <span className="text-2xl font-bold text-white">{settings.ios?.version || 'N/A'}</span>
              </div>
              {settings.ios?.forceUpdate && (
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-rose-400">
                  <Lock className="h-3 w-3" /> Force Update Active
                </span>
              )}
            </SectionCard>

            <SectionCard title="Last Updated" description="Settings changed">
              <p className="text-lg text-white">
                {settings.updatedAt ? formatDateTime(
                  typeof settings.updatedAt === 'object' && 'seconds' in settings.updatedAt
                    ? new Date((settings.updatedAt as any).seconds * 1000).toISOString()
                    : String(settings.updatedAt)
                ) : 'Never'}
              </p>
            </SectionCard>
          </div>

          {/* Platform Settings */}
          <div className="grid gap-6 md:grid-cols-2">
            <SectionCard title="Android Settings">
              <VersionEditor platform="android" data={androidData} onChange={setAndroidData} />
            </SectionCard>
            <SectionCard title="iOS Settings">
              <VersionEditor platform="ios" data={iosData} onChange={setIosData} />
            </SectionCard>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveVersion}
              disabled={saving}
              className="flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-white hover:bg-orange-600 transition disabled:opacity-50"
            >
              {saving ? (<RefreshCw className="h-4 w-4 animate-spin" />) : (<Save className="h-4 w-4" />)}
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </>
      )}

      {/* === ADMIN CONTACT TAB === */}
      {activeTab === 'contact' && (
        <div className="grid gap-6">
          <div className="flex items-start gap-4 rounded-3xl border border-orange-500/20 bg-orange-500/5 p-5">
            <div className="rounded-xl bg-orange-500/10 p-2.5 shrink-0">
              <MessageSquare className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="font-semibold text-orange-300 text-sm">Single Source of Truth</p>
              <p className="text-stone-400 text-sm mt-1 leading-relaxed">
                Data disimpan di <code className="text-orange-400 bg-stone-900 px-1 rounded">Firestore: systemConfig/adminContact</code> dan dibaca langsung oleh aplikasi mobile di halaman <strong>Contact Support</strong>.
              </p>
            </div>
          </div>

          <SectionCard 
            title="Admin Dashboard Contact"
            description="Informasi kontak yang tampil di halaman Contact Support aplikasi mobile."
            action={
              <button
                onClick={saveAdminContact}
                disabled={savingContact}
                className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600 transition disabled:opacity-50"
              >
                <Save className="h-4 w-4" /> {savingContact ? 'Menyimpan...' : 'Simpan Kontak'}
              </button>
            }
          >
            {loadingContact ? (
              <div className="py-6 text-stone-500 italic">Memuat data kontak...</div>
            ) : (
              <div className="grid gap-5 py-2 md:grid-cols-2">
                <label className="block space-y-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-stone-300"><User className="h-4 w-4 text-stone-500" /> Nama Tim / Company</span>
                  <input type="text" value={adminContact.name} onChange={e => setAdminContact(p => ({ ...p, name: e.target.value }))}
                    className="w-full rounded-2xl border border-stone-800 bg-[#141414] px-4 py-3 text-sm text-stone-100 outline-none focus:border-orange-500/50"
                    placeholder="Parlent Support Team" />
                </label>
                <label className="block space-y-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-stone-300"><Mail className="h-4 w-4 text-stone-500" /> Email Utama</span>
                  <input type="email" value={adminContact.email} onChange={e => setAdminContact(p => ({ ...p, email: e.target.value }))}
                    className="w-full rounded-2xl border border-stone-800 bg-[#141414] px-4 py-3 text-sm text-stone-100 outline-none focus:border-orange-500/50"
                    placeholder="admin@parlent.com" />
                </label>
                <label className="block space-y-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-stone-300"><Mail className="h-4 w-4 text-stone-500" /> Email Support (untuk user)</span>
                  <input type="email" value={adminContact.supportEmail} onChange={e => setAdminContact(p => ({ ...p, supportEmail: e.target.value }))}
                    className="w-full rounded-2xl border border-stone-800 bg-[#141414] px-4 py-3 text-sm text-stone-100 outline-none focus:border-orange-500/50"
                    placeholder="support@parlent.com" />
                </label>
                <label className="block space-y-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-stone-300"><Phone className="h-4 w-4 text-stone-500" /> Nomor Telepon</span>
                  <input type="text" value={adminContact.phone} onChange={e => setAdminContact(p => ({ ...p, phone: e.target.value }))}
                    className="w-full rounded-2xl border border-stone-800 bg-[#141414] px-4 py-3 text-sm text-stone-100 outline-none focus:border-orange-500/50"
                    placeholder="+62 812-3456-7890" />
                </label>
                <label className="block space-y-2 md:col-span-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-stone-300"><MessageSquare className="h-4 w-4 text-stone-500" /> WhatsApp Link (opsional)</span>
                  <input type="text" value={adminContact.whatsapp} onChange={e => setAdminContact(p => ({ ...p, whatsapp: e.target.value }))}
                    className="w-full rounded-2xl border border-stone-800 bg-[#141414] px-4 py-3 text-sm text-stone-100 outline-none focus:border-orange-500/50"
                    placeholder="https://wa.me/62812345678" />
                </label>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Preview Mobile App" description="Tampilan yang akan muncul di halaman Contact Support.">
            <div className="mt-2 rounded-3xl bg-black border border-stone-700 p-6 max-w-sm mx-auto">
              <p className="text-xs text-stone-500 uppercase tracking-widest font-bold mb-4">Preview — Contact Support</p>
              <div className="space-y-3">
                {adminContact.name && (
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-900">
                    <div className="h-9 w-9 rounded-full bg-orange-500/20 flex items-center justify-center"><User className="h-4 w-4 text-orange-400" /></div>
                    <div><p className="text-xs text-stone-500">Tim Support</p><p className="text-sm font-semibold text-white">{adminContact.name}</p></div>
                  </div>
                )}
                {adminContact.supportEmail && (
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-900">
                    <div className="h-9 w-9 rounded-full bg-blue-500/20 flex items-center justify-center"><Mail className="h-4 w-4 text-blue-400" /></div>
                    <div><p className="text-xs text-stone-500">Email</p><p className="text-sm text-white">{adminContact.supportEmail}</p></div>
                  </div>
                )}
                {adminContact.phone && (
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-stone-900">
                    <div className="h-9 w-9 rounded-full bg-emerald-500/20 flex items-center justify-center"><Phone className="h-4 w-4 text-emerald-400" /></div>
                    <div><p className="text-xs text-stone-500">Telepon</p><p className="text-sm text-white">{adminContact.phone}</p></div>
                  </div>
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* === WATCH MODELS TAB === */}
      {activeTab === 'watch-models' && (
        <div className="grid gap-6">
          <div className="flex items-start gap-4 rounded-3xl border border-orange-500/20 bg-orange-500/5 p-5">
            <div className="shrink-0 rounded-xl bg-orange-500/10 p-2.5">
              <Watch className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-orange-300">Dasar metrik Users Parlent</p>
              <p className="mt-1 text-sm leading-relaxed text-stone-400">
                Aplikasi mengirim nama jam apa adanya setiap kali terhubung. Daftar di bawah
                menentukan nama mana yang dihitung sebagai jam Parlent di halaman Users.
                Menambah model baru di sini berlaku langsung, tanpa rilis ulang aplikasi.
              </p>
            </div>
          </div>

          <WatchModelsEditor
            models={settings.watchModels?.parlent ?? DEFAULT_PARLENT_WATCH_MODELS}
            saving={saving}
            onSave={(models) => saveSettings({ watchModels: { parlent: models } })}
          />
        </div>
      )}
    </div>
  )
}
